<script setup lang="ts">
import { useFuse } from '@vueuse/integrations/useFuse'
import { MagnifyIcon } from 'mdi-vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { ACheckboxInput, ARadioInput, type RadioInputOption } from '../generics'
import type { TableFilterOptions, TableFilterValueOptions } from '.'

const query = defineModel<string>('search', { default: '' })
const model = defineModel<TableFilterValueOptions>({ default: { operator: 'equal', values: [] }, required: true })

const properties = defineProps<{
  filter: TableFilterOptions
}>()

const options = computed((): RadioInputOption[] =>
  Object.entries(properties.filter.options ?? {}).map(([value, label]) => ({ label: label as string, value })),
)
const { results } = useFuse(query, options, { fuseOptions: { keys: ['label'] } })
const items = computed((): RadioInputOption[] => (query.value.length > 0 ? results.value?.map((result) => result.item) : options.value))

function isChecked(value: string): boolean {
  return model.value?.values.includes(value) ?? false
}
function onChange(value: string): void {
  model.value.values = isChecked(value) ? model.value.values.filter((v) => v !== value) : [...model.value.values, value]
}

const { t } = useI18n({
  messages: {
    en: {
      search: 'Search',
      'no results': 'No results found',
    },
    nl: {
      search: 'Zoeken',
      'no results': 'Geen resultaten gevonden',
    },
  },
})
</script>

<template>
  <div class="atabix-table-filter-section">
    <div class="atabix-table-filter-search">
      <MagnifyIcon class="atabix-table-filter-search-icon" />
      <input
        ref="input"
        v-model="query"
        :aria-label="t('search')"
        class="atabix-table-filter-search-input"
        maxlength="45"
        :placeholder="t('search')"
        type="search"
      />
    </div>
  </div>
  <div class="atabix-table-filter-section space-y-4">
    <template v-if="items.length === 0">
      <p class="text-gray-500">{{ t('no results') }}</p>
    </template>
    <template v-else>
      <ARadioInput v-if="!filter.multiple" :items="items" :model-value="model.values[0]" @update:model-value="model.values = [$event]" />
      <template v-else>
        <ACheckboxInput
          v-for="{ label, value } in items"
          :id="value"
          :key="value"
          :label="label"
          :model-value="isChecked(value)"
          type="checkbox"
          @update:model-value="onChange(value)"
        />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss"></style>
